import React, { useState, useRef, useEffect } from 'react';
import { X, Megaphone, Image, Type, Video, FileText, Eye, Send } from 'lucide-react';

// Export the interface so it can be imported if needed, or just used here
export interface CtaData {
  headerType: HeaderType;
  headerContent: string;
  bodyText: string;
  footerText: string;
  buttonDisplayText: string;
  buttonUrl: string;
  displayText?: string; // Helper for display in chat
}

interface CtaModalProps {
  isOpen?: boolean; // Add isOpen as optional prop
  onClose: () => void;
  onSend: (cta: any) => void; // Use any or specific type matching the sender
}

type HeaderType = 'none' | 'text' | 'image' | 'video' | 'document';

const CTA_TEMPLATES = [
  {
    id: 'cta1',
    title: '限时优惠 (图片页眉)',
    data: {
      headerType: 'image' as HeaderType,
      headerContent: 'https://images.unsplash.com/photo-1526947425960-945c6e72858f?w=500',
      bodyText: '🎉 限时优惠！现在购买我们的明星产品可享受 8 折优惠。不要错过！',
      footerText: '优惠至本月底截止',
      buttonDisplayText: '立即抢购',
      buttonUrl: 'https://example.com/sale',
    },
  },
  {
    id: 'cta2',
    title: '新品发布 (文本页眉)',
    data: {
      headerType: 'text' as HeaderType,
      headerContent: '🚀 激动人心的新品发布！',
      bodyText: '探索我们的最新系列，专为您设计。',
      footerText: '引领潮流',
      buttonDisplayText: '了解更多',
      buttonUrl: 'https://example.com/new',
    },
  },
  {
    id: 'cta3',
    title: '网站引流 (无页眉)',
    data: {
      headerType: 'none' as HeaderType,
      headerContent: '',
      bodyText: '👋 您好！访问我们的官方网站，发现更多精彩内容和独家资讯。',
      footerText: '',
      buttonDisplayText: '访问网站',
      buttonUrl: 'https://example.com',
    },
  },
];

const CtaModal: React.FC<CtaModalProps> = ({ onClose, onSend }) => {
  // 从localStorage恢复上次编辑的内容
  const getStoredCtaData = () => {
    try {
      const stored = localStorage.getItem('lastCtaData');
      return stored ? JSON.parse(stored) : {
        headerType: 'none' as HeaderType,
        headerContent: '',
        bodyText: '',
        footerText: '',
        buttonDisplayText: '',
        buttonUrl: '',
        selectedTemplateId: null
      };
    } catch {
      return {
        headerType: 'none' as HeaderType,
        headerContent: '',
        bodyText: '',
        footerText: '',
        buttonDisplayText: '',
        buttonUrl: '',
        selectedTemplateId: null
      };
    }
  };

  const storedData = getStoredCtaData();
  const [headerType, setHeaderType] = useState<HeaderType>(storedData.headerType);
  const [headerContent, setHeaderContent] = useState(storedData.headerContent);
  const [bodyText, setBodyText] = useState(storedData.bodyText);
  const [footerText, setFooterText] = useState(storedData.footerText);
  const [buttonDisplayText, setButtonDisplayText] = useState(storedData.buttonDisplayText);
  const [buttonUrl, setButtonUrl] = useState(storedData.buttonUrl);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(storedData.selectedTemplateId);

  const modalRef = useRef<HTMLDivElement>(null);

  // 保存编辑内容到localStorage
  React.useEffect(() => {
    const ctaData = {
      headerType,
      headerContent,
      bodyText,
      footerText,
      buttonDisplayText,
      buttonUrl,
      selectedTemplateId
    };
    localStorage.setItem('lastCtaData', JSON.stringify(ctaData));
  }, [headerType, headerContent, bodyText, footerText, buttonDisplayText, buttonUrl, selectedTemplateId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleTemplateSelect = (template: typeof CTA_TEMPLATES[0]) => {
    setSelectedTemplateId(template.id);
    setHeaderType(template.data.headerType);
    setHeaderContent(template.data.headerContent);
    setBodyText(template.data.bodyText);
    setFooterText(template.data.footerText);
    setButtonDisplayText(template.data.buttonDisplayText);
    setButtonUrl(template.data.buttonUrl);
  };

  const handleSend = () => {
    if (!bodyText.trim() || !buttonDisplayText.trim() || !buttonUrl.trim()) {
      alert('正文、按钮文本和按钮链接是必填项。');
      return;
    }

    const apiPayload = {
      messaging_product: "whatsapp",
      recipient_type: "individual",
      to: "<WHATSAPP_USER_PHONE_NUMBER>", // This would be dynamically replaced by the backend
      type: "interactive",
      interactive: {
        type: "cta_url",
        header:
          headerType !== 'none' && headerContent
            ? {
              type: headerType,
              ...(headerType === 'text' ? { text: headerContent } : { [headerType]: { link: headerContent } }),
            }
            : undefined,
        body: {
          text: bodyText,
        },
        footer: footerText ? { text: footerText } : undefined,
        action: {
          name: "cta_url",
          parameters: {
            display_text: buttonDisplayText,
            url: buttonUrl,
          },
        },
      },
    };

    // Send both the API payload and a display text for the UI
    onSend({
      ...apiPayload,
      displayText: buttonDisplayText
    });

    // 可选：发送后清空内容（如果用户需要的话）
    // setHeaderType('none');
    // setHeaderContent('');
    // setBodyText('');
    // setFooterText('');
    // setButtonDisplayText('');
    // setButtonUrl('');
    // setSelectedTemplateId(null);

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-card border border-border rounded-lg shadow-2xl shadow-primary/20 w-full max-w-4xl flex flex-col animate-slide-in h-[90vh]"
      >
        <header className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-accent" />
            发送 CTA (Call-To-Action) 消息
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X />
          </button>
        </header>
        <main className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Editor Column */}
          <div className="flex-1 overflow-y-auto p-6 border-r border-border">
            <div className="space-y-6">
              {/* Templates */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">快速模板</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {CTA_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-3 border rounded-md text-left text-xs hover:bg-muted/50 transition-all ${selectedTemplateId === template.id ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-border'
                        }`}
                    >
                      <div className="font-medium mb-1">{template.title}</div>
                      <div className="text-muted-foreground truncate">{template.data.bodyText}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Header Config */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-muted-foreground">页眉 (可选)</label>
                <div className="flex gap-2 p-1 bg-muted/30 rounded-lg w-fit">
                  {[
                    { type: 'none', icon: X, label: '无' },
                    { type: 'text', icon: Type, label: '文本' },
                    { type: 'image', icon: Image, label: '图片' },
                    { type: 'video', icon: Video, label: '视频' },
                    { type: 'document', icon: FileText, label: '文档' },
                  ].map((opt) => (
                    <button
                      key={opt.type}
                      onClick={() => setHeaderType(opt.type as HeaderType)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs transition-all ${headerType === opt.type
                          ? 'bg-background shadow-sm text-foreground font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                      <opt.icon className="w-3 h-3" />
                      {opt.label}
                    </button>
                  ))}
                </div>
                {headerType !== 'none' && (
                  <input
                    type="text"
                    value={headerContent}
                    onChange={(e) => setHeaderContent(e.target.value)}
                    placeholder={headerType === 'text' ? "输入页眉文本..." : "输入媒体 URL..."}
                    className="w-full p-2 bg-input border border-border rounded-md focus:ring-2 focus:ring-accent/50 focus:outline-none text-sm"
                  />
                )}
              </div>

              {/* Body Config */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">正文内容 <span className="text-destructive">*</span></label>
                <textarea
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  rows={4}
                  placeholder="输入主要消息内容..."
                  className="w-full p-2 bg-input border border-border rounded-md focus:ring-2 focus:ring-accent/50 focus:outline-none text-sm resize-none"
                />
              </div>

              {/* Footer Config */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">页脚 (可选)</label>
                <input
                  type="text"
                  value={footerText}
                  onChange={(e) => setFooterText(e.target.value)}
                  placeholder="例如：优惠截止日期、版权信息..."
                  className="w-full p-2 bg-input border border-border rounded-md focus:ring-2 focus:ring-accent/50 focus:outline-none text-sm"
                />
              </div>

              {/* Button Config */}
              <div className="p-4 border border-border rounded-lg bg-muted/10 space-y-4">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-accent" />
                  行动按钮 (CTA) 配置
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">按钮文本 <span className="text-destructive">*</span></label>
                    <input
                      type="text"
                      value={buttonDisplayText}
                      onChange={(e) => setButtonDisplayText(e.target.value)}
                      placeholder="例如：访问官网"
                      className="w-full p-2 bg-input border border-border rounded-md focus:ring-2 focus:ring-accent/50 focus:outline-none text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">目标 URL <span className="text-destructive">*</span></label>
                    <input
                      type="text"
                      value={buttonUrl}
                      onChange={(e) => setButtonUrl(e.target.value)}
                      placeholder="https://..."
                      className="w-full p-2 bg-input border border-border rounded-md focus:ring-2 focus:ring-accent/50 focus:outline-none text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Column - 移动端隐藏 */}
          <div className="hidden md:flex w-full md:w-[320px] bg-muted/20 p-6 flex-col items-center justify-center border-l border-border">
            <div className="w-full max-w-[280px]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  预览效果
                </span>
              </div>

              {/* WhatsApp Message Bubble Preview */}
              <div className="bg-[#202c33] rounded-lg overflow-hidden shadow-lg text-[#e9edef] text-sm relative">
                {/* Header */}
                {headerType === 'image' && headerContent && (
                  <div className="h-32 bg-gray-700 w-full overflow-hidden">
                    <img src={headerContent} alt="Header" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
                  </div>
                )}
                {headerType === 'text' && headerContent && (
                  <div className="px-3 pt-3 font-bold">{headerContent}</div>
                )}

                {/* Body */}
                <div className="p-3 whitespace-pre-wrap">
                  {bodyText || <span className="text-gray-500 italic">正文内容...</span>}
                </div>

                {/* Footer */}
                {footerText && (
                  <div className="px-3 pb-3 text-xs text-gray-400">
                    {footerText}
                  </div>
                )}

                {/* Button */}
                <div className="border-t border-[#2a3942] p-3 flex items-center justify-center gap-2 text-[#53bdeb] font-medium cursor-pointer bg-[#202c33] hover:bg-[#2a3942] transition-colors">
                  <Megaphone className="w-4 h-4" />
                  {buttonDisplayText || '按钮文本'}
                </div>
              </div>

              <p className="text-xs text-center text-muted-foreground mt-4">
                * 预览仅供参考，实际显示效果可能因设备而异。
              </p>
            </div>
          </div>
        </main>
        <footer className="flex justify-end gap-3 p-4 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-muted transition-colors">
            取消
          </button>
          <button
            onClick={handleSend}
            className="px-6 py-2 text-sm font-medium bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-green/90 transition-colors flex items-center gap-2 shadow-lg shadow-whatsapp-green/20"
          >
            <Send className="w-4 h-4" />
            发送消息
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CtaModal;
